/**
 * Deliverable renderer registry (ARCH-005 §2).
 *
 * `renderers[content.kind] ?? MarkdownRenderer`. Each renderer reads the
 * discriminated DeliverableContent variant from @studio/api-schemas (the
 * payload schemas live in api-schemas, the codebase's chosen home; ARCH-005
 * names packages/api-contracts/ which does not exist here, reconciled by the
 * ARCH-005 2026-05-15 amendment). Renderers render ONLY the body; the
 * meta-note + sources + video are DeliverableLayout's job (ARCH-005 §3).
 *
 * The per-type renderers are colocated under
 * components/portal/renderers/<task_type>/ (ARCH-005 §9: index.tsx +
 * payload.ts). Adding a new templated type is: copy a directory, add its
 * payload re-export, add one switch arm here. No other registry change is
 * needed, and no existing renderer file is touched (ARCH-005 §9 / Positive).
 *
 * verbosity (coaching depth) is accepted by every renderer; V1 interpretation
 * is documented per ARCH-005 §5 ('recommend' tightens, 'show_me' is full,
 * 'build_it_with_me' adds methodology callouts).
 */

import { useState } from 'react';
import type {
  DeliverableContent,
  GenericMarkdownContent,
} from '@studio/api-schemas';
import { Markdown } from './Markdown';
import type { RendererProps, Verbosity } from './shared';
import { CompetitiveLandscapeRenderer } from './competitive_landscape';
import { IcpSynthesisRenderer } from './icp_synthesis';
import { VoiceEncodingRenderer } from './voice_encoding';
import { MessagingArchitectureRenderer } from './messaging_architecture';
import { PitchDeckReviewRenderer } from './pitch_deck_review';
import { OsHandoffRenderer } from './os_handoff';

export type { Verbosity } from './shared';

/**
 * GenericMarkdownContent renderer (T-082 verbosity wiring).
 *
 * The body_md is split on the first horizontal-rule break (--- on its own
 * line) or, when none is present, into a leading head (first ~40% of lines)
 * and a trailing tail. At verbosity='recommend' we render only the head and
 * surface a "Read more" disclosure for the tail. At 'show_me' and
 * 'build_it_with_me' we render the full body. This is the lightest meaningful
 * variant the markdown fallback can carry without changing the content shape,
 * matching ARCH-005 §5's "light touch so two renderers never disagree wildly
 * on the same tier" guidance.
 */
function MarkdownRenderer({
  payload,
  verbosity,
}: RendererProps<GenericMarkdownContent>) {
  const { head, tail } = splitForVerbosity(payload.body_md);
  const [expanded, setExpanded] = useState(false);
  const tightened = verbosity === 'recommend' && tail.length > 0;
  return (
    <div className="space-y-3">
      <Markdown source={head} />
      {tightened && !expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm font-medium text-[color:var(--color-navy)] underline underline-offset-4 hover:opacity-80"
        >
          Read more
        </button>
      ) : null}
      {tail.length > 0 && (!tightened || expanded) ? (
        <Markdown source={tail} />
      ) : null}
    </div>
  );
}

function splitForVerbosity(body: string): { head: string; tail: string } {
  const normalized = body.replace(/\r\n/g, '\n');
  const lines = normalized.split('\n');
  const hrIdx = lines.findIndex((l) => /^\s*---\s*$/.test(l));
  if (hrIdx > 0) {
    return {
      head: lines.slice(0, hrIdx).join('\n'),
      tail: lines.slice(hrIdx + 1).join('\n'),
    };
  }
  // No explicit break: split at the first blank line after roughly 40% of the
  // body. Falls back to the full body as head when the source is short.
  if (lines.length < 6) return { head: normalized, tail: '' };
  const target = Math.max(3, Math.floor(lines.length * 0.4));
  let cut = -1;
  for (let i = target; i < lines.length; i += 1) {
    if ((lines[i] ?? '').trim() === '') {
      cut = i;
      break;
    }
  }
  if (cut < 0) return { head: normalized, tail: '' };
  return {
    head: lines.slice(0, cut).join('\n'),
    tail: lines.slice(cut + 1).join('\n'),
  };
}

/**
 * Page-level entry. Looks up the renderer by content.kind and falls back to
 * the markdown renderer for any kind without a templated component (ARCH-005
 * §2). Returns the BODY only; wrap it in DeliverableLayout.
 */
export function DeliverableBody({
  content,
  verbosity,
}: {
  content: DeliverableContent;
  verbosity: Verbosity;
}) {
  switch (content.kind) {
    case 'competitive_landscape':
      return (
        <CompetitiveLandscapeRenderer
          payload={content}
          verbosity={verbosity}
        />
      );
    case 'icp_synthesis':
      return <IcpSynthesisRenderer payload={content} verbosity={verbosity} />;
    case 'voice_encoding':
      return <VoiceEncodingRenderer payload={content} verbosity={verbosity} />;
    case 'messaging_architecture':
      return (
        <MessagingArchitectureRenderer
          payload={content}
          verbosity={verbosity}
        />
      );
    case 'pitch_deck_review':
      return (
        <PitchDeckReviewRenderer payload={content} verbosity={verbosity} />
      );
    case 'os_handoff':
      return <OsHandoffRenderer payload={content} verbosity={verbosity} />;
    case 'generic_markdown':
      return <MarkdownRenderer payload={content} verbosity={verbosity} />;
    default: {
      // Exhaustiveness guard: any new kind without a renderer falls back to
      // markdown if it carries body_md, otherwise renders a safe notice. This
      // is the markdown fallback half of the registry (ARCH-005 §2): the 11
      // non-Phase-1 templated types + the 'other' catch-all land here until
      // they graduate (PRD-STATUS Decision 1).
      const fallback = content as { body_md?: unknown };
      if (typeof fallback.body_md === 'string') {
        return <Markdown source={fallback.body_md} />;
      }
      return (
        <p className="text-sm text-[color:var(--color-text-mute)]">
          This deliverable type does not have a renderer yet.
        </p>
      );
    }
  }
}
